import React from "react";
import { DotLegalAlert, DotLegalDialog, DotLegalHeader, DotLegalTextField } from "@dotlegal/dotlegal-ui-components";
import { useTranslation } from "../../localization/useTranslation";
import { useAddBestPracticeDocumentTemplateDialog, AddBestPracticeDocumentTemplateDialogProps } from "./AddBestPracticeDocumentTemplateDialog.hooks";
import DotLegalFileUploadWrapper from "../../common/components/dotLegalFileUpload/DotLegalFileUploadWrapper";
import { isNullOrWhitespace } from "../../common/stringOperations";
import { Box } from "@mui/material";
import { useAiSpendingWarning } from "../../aiAdministration/AiAdministration.hooks";
import { isAiSpendingExhausted } from "../../aiAdministration/AiAdministration.types";
import AiSpendingWarningAlert from "../../aiAdministration/AiSpendingWarningAlert";
import DialogHeaderWithInformation from "@common/components/dialogHeaderWithInformation/DialogHeaderWithInformation";
import UploadProgressBar from "@common/components/uploadProgress/UploadProgressBar";

function AddBestPracticeDocumentTemplateDialog(props: AddBestPracticeDocumentTemplateDialogProps) {
    const { translateString } = useTranslation();
    const { viewModel, inProgress, uploadPercentage, showErrors, onOkClick, onNameChange, onFileChange } = useAddBestPracticeDocumentTemplateDialog(props);
    const { data: spendingWarning } = useAiSpendingWarning();
    const isAiExhausted = isAiSpendingExhausted(spendingWarning);

    return (
        <DotLegalDialog
            header={
                <DialogHeaderWithInformation
                    header={translateString("createBestPracticeDocumentTemplate")}
                    information={translateString("addBestPracticeDocumentTemplateInfo")}
                />
            }
            size="sm"
            buttonOkText={translateString("create")}
            open
            onDialogClose={props.onDialogClose}
            onOkClick={onOkClick}
            inProgress={inProgress}
            disableBackdropOnCloseIfValueChange={viewModel}
            okButtonDisabled={isAiExhausted}
        >
            <Box sx={{ display: "flex", justifyContent: "center" }}>
                <DotLegalHeader headerStyle={"small"} fontWeight={400} marginBottom={2} center>
                    {translateString("addBestPracticeDocumentTemplateInfo")}
                </DotLegalHeader>
            </Box>

            <Box sx={{ mb: 2 }}>
                <DotLegalTextField
                    debounce={false}
                    label={translateString("name")}
                    value={viewModel.name}
                    onChange={onNameChange}
                    errorText={showErrors && isNullOrWhitespace(viewModel.name) ? translateString("fieldRequired") : undefined}
                    disabled={isAiExhausted}
                />
            </Box>

            <DotLegalFileUploadWrapper
                errorText={showErrors && isNullOrWhitespace(viewModel.fileName) ? translateString("fieldRequired") : undefined}
                files={isNullOrWhitespace(viewModel.fileName) ? [] : [viewModel.fileName]}
                onFilesChanged={onFileChange}
                isLoading={false}
                disabled={isAiExhausted}
            />

            <UploadProgressBar isUploading={inProgress} uploadPercentage={uploadPercentage} />

            {inProgress && (
                <Box sx={{ marginTop: 2 }}>
                    <DotLegalAlert
                        severity="info"
                        warningHeader={translateString("aiQualityAssuranceAnalyzingDocument")}
                        warningText={translateString("extractingContractInfo")}
                    />
                </Box>
            )}

            <AiSpendingWarningAlert warning={spendingWarning} />
        </DotLegalDialog>
    );
}

export default AddBestPracticeDocumentTemplateDialog;
